'use client';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useEffect, useState } from 'react';
import { SourceObject, Slide, TimezoneDetails, MediaObject, Source } from '@/shared/types';
import { getSourceInfoFromServer, getTimeFromStr } from './functions';
import { extractPathAfterBulk } from './functions';

dayjs.extend(utc);
dayjs.extend(timezone);

export const SOURCE_INFO_TTL_MS = 900000; // 15 minutes

export const getCurrentSourceInfo = async (sourceFilePath: string) => {
  const sourceInfoString = sessionStorage.getItem(`SI_${sourceFilePath}`);
  try {
    let sourceInfoObject = sourceInfoString ? JSON.parse(sourceInfoString) : undefined;
    if (!sourceInfoObject || (sourceInfoObject?.exp && new Date().getTime() > sourceInfoObject?.exp)) {
      const sourceInfo = await getSourceInfoFromServer(sourceFilePath);
      sourceInfo.exp = new Date().getTime() + SOURCE_INFO_TTL_MS;
      sessionStorage.setItem(`SI_${sourceFilePath}`, JSON.stringify(sourceInfo));
      sourceInfoObject = sourceInfo;
    }
    return sourceInfoObject;
  } catch (error) {
    return null;
  }
}

export const entityEventsToSlides = (eventsToParse: any[], sources: Source[], allSourcesFromChat: any) => {
  return eventsToParse.map((event: any, index: number) => {
    let sourcesForEvent: SourceObject[] = [];
    if (event.sources) {
      const sourcesFromEntityService = sources.map((source: Source) => {
        return {
          sourceType: extractPathAfterBulk(source.sourceFilePath || '').sourceType || 'Unknown',
          sourceUrl: source.sourceFilePath,
        }
      });
      sourcesForEvent = dedupeSources(sourcesFromEntityService);
    }

    // get the last 5 sources from allSourcesFromChat
    const numberOfExtraSourcesToInclude = 5;
    const extraSources = allSourcesFromChat?.allSources?.slice(0, numberOfExtraSourcesToInclude) || [];
    // get the last mediaSource from allSourcesFromChat
    const numberOfExtraMediaSourcesToInclude = 1;
    const extraMediaSources = allSourcesFromChat?.mediaSources?.slice(0,numberOfExtraMediaSourcesToInclude) || [];

    const sourcesToInclude = [ ...(sourcesForEvent || []), ...(extraSources || [])].slice(0, 5);
    const mediaSourcesToInclude = [ ...(event.media || []), ...(extraMediaSources || [])];
    const description = event.rawTexts.join('\n');
    const newEvent: Slide = {
      show: true,
      approval: 'approved',
      slideTitle: event.eventSummary,
      slideDescription: description,
      date: event.eventDate,
      media: mediaSourcesToInclude,
      sources: sourcesToInclude,
      location: event.location,
      eventLocalTimezone: event.eventLocalTimezone,
    }
    return newEvent;
  });
}

export function useWindowSize() {
  const [windowSize, setWindowSize] = useState<{ width: number | undefined, height: number | undefined }>({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    let raf = 0;
    function handleResize() {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return windowSize;
}

export const getDescriptionFromAnswerText = (answerText: string) => {
  const description = answerText.replace(/<[^>]*>/g, '');
  return description;
}

export const getTitleAndDescriptionFromAnswerText = (answerText: string) => {
  const firstLine = answerText.split('\n')[0];
  const title = firstLine.replace(/^#\s*/, '');
  const description = answerText.split('\n').
    slice(1).join('\n').
    replace(/##\s*/, '').
    replace(/<[^>]*>/g, '');
  return { title, description };
}

export const dedupeSources = (sourceObjects: SourceObject[]) => {
  if(!sourceObjects || sourceObjects.length === 0) return [];

  const seen = new Map<string, SourceObject>();

  sourceObjects.forEach(source => {
    // Create a unique key based on the important properties
    const key = JSON.stringify({
      sourceUrl: source.sourceUrl,
      sourceType: source.sourceType,
      pageNumber: source.pageNumber || source.sourceContext?.pageNumber,
      startTime: source.sourceContext?.startTime,
      endTime: source.sourceContext?.endTime
    });
    // Only add if we haven't seen this exact combination
    if (!seen.has(key)) {
      seen.set(key, source);
    }
  });

  return Array.from(seen.values());
}

export const dedupeMediaSources = (mediaSources: MediaObject[]) => {
  if(!mediaSources || mediaSources.length === 0) return [];

  const seen = new Map<string, MediaObject>();

  mediaSources.forEach(media => {
    // Create a unique key based on the important properties
    const key = JSON.stringify({
      mediaUrl: media.mediaUrl || media.mediaSrc,
      mediaType: media.mediaType,
      pageNumber: media.mediaContext?.pageNumber || media.mediaContext?.page,
      startTime: media.mediaContext?.startTime,
      endTime: media.mediaContext?.endTime
    });

    // Only add if we haven't seen this exact combination
    if (!seen.has(key)) {
      seen.set(key, media);
    }
  });

  return Array.from(seen.values());
}

export function bytesToSize(usageInBytes: number) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  if (usageInBytes === 0) return 'n/a';
  const i = Math.floor(Math.log(usageInBytes) / Math.log(1024));
  if (i === 0) return `${usageInBytes} ${sizes[i]})`;
  return `${(usageInBytes / (1024 ** i)).toFixed(1)} ${sizes[i]}`;
}

// Parse times into hours and minutes
export const parseTime = (timeStr: string) => {
  const [time, modifier] = timeStr.split(/\s+/);
  let [hours, minutes] = time.split(':').map(Number);
  if (modifier) {
    if (modifier.toLowerCase() === 'pm' && hours !== 12) hours += 12;
    if (modifier.toLowerCase() === 'am' && hours === 12) hours = 0;
  }
  return hours * 60 + (minutes || 0);
};

export const formatTime: (value: number, concise?: boolean, hoursOnly?: boolean) => string = (value: number, concise = false, hoursOnly = false) => {
  const MINUTES_IN_HOUR = 60;
  const MINUTES_IN_DAY = MINUTES_IN_HOUR * 24;
  const MINUTES_IN_WEEK = MINUTES_IN_DAY * 7;
  const MINUTES_IN_MONTH = MINUTES_IN_DAY * 30; // Approximation
  const MINUTES_IN_YEAR = MINUTES_IN_DAY * 365; // Approximation

  const years = Math.floor(value / MINUTES_IN_YEAR);
  const months = Math.floor((value % MINUTES_IN_YEAR) / MINUTES_IN_MONTH);
  const weeks = Math.floor((value % MINUTES_IN_MONTH) / MINUTES_IN_WEEK);
  const days = Math.floor((value % MINUTES_IN_WEEK) / MINUTES_IN_DAY);
  const hours = Math.floor((value % MINUTES_IN_DAY) / MINUTES_IN_HOUR);
  const minutes = Math.ceil(value % MINUTES_IN_HOUR);

  if (hoursOnly) {
    const totalHours = Math.floor(value / MINUTES_IN_HOUR);
    return `${totalHours.toLocaleString()} hour${totalHours !== 1 ? 's' : ''}`;
  }

  const parts = [];
  if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`);
  if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
  if (weeks > 0) parts.push(`${weeks} week${weeks !== 1 ? 's' : ''}`);
  if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);

  if (concise) {
    return `More than ${parts.slice(0, 2).join(' and ')}`;
  }

  if (parts.length > 1) {
    const lastPart = parts.pop();
    return `${parts.join(', ')}, and ${lastPart}`;
  }

  return parts[0];
};

export const getDisplayDateAndTime = (eventDateTime: string, eventLocalTimezone: string | null): { displayDate: string | undefined, time: string | undefined, localizedDateTime: string | undefined } => {
  let displayDate = undefined;
  let time = undefined;
  let localizedDateTime = undefined;

  if (eventDateTime && eventLocalTimezone) {
    localizedDateTime = dayjs(eventDateTime).tz(eventLocalTimezone).format('YYYY-MM-DD hh:mm:ss A');
//    localizedDateTime = dayjs(eventDateTime).utc().format('YYYY-MM-DD hh:mm:ss A');
  } else if (eventDateTime) {
    localizedDateTime = dayjs(eventDateTime).utc().format('YYYY-MM-DD hh:mm:ss A');
  }

  if (eventLocalTimezone) { // we have a timzone with the dateTime, so use it
    displayDate = eventDateTime ? dayjs(eventDateTime).tz(eventLocalTimezone).format('YYYY-MM-DD') : undefined;
    time = eventDateTime ? dayjs(eventDateTime).tz(eventLocalTimezone).format('hh:mm:ss A') : undefined;
  } else { // no timezone, so we probably want date and time in UTC without any offset
    displayDate = eventDateTime ? dayjs(eventDateTime).utc().format('YYYY-MM-DD') : undefined;
    time = eventDateTime ? dayjs(eventDateTime).utc().format('hh:mm:ss A') : undefined;
  }

  return { displayDate, time, localizedDateTime };
}

export const stringifyLocation = (location: { coordinates: [number, number] | null; name: string | null; address: string | null; } | null | undefined) => {
  if (!location) return '--';
  if (location.address) return JSON.stringify(location.address);
  if (location.name) return JSON.stringify(location.name);
  if (location.coordinates) return JSON.stringify(location.coordinates);
  return '--';
}

export const sortByRealDatetime = (a: Slide, b: Slide, timezoneListByName: Record<string, TimezoneDetails>) => {
  const idMod = (a.id || '') < (b.id || '') ? -1 : 1;
  if (!a.date && !b.date) return idMod;
  if (!a.date) return -1;
  if (!b.date) return 1;
  const dataAOffset = timezoneListByName[a.eventLocalTimezone || '']?.offsetInMilliseconds;
  const dataBOffset = timezoneListByName[b.eventLocalTimezone || '']?.offsetInMilliseconds;
  const dateAMilliseconds = new Date(a.date).getTime();
  const dateBMilliseconds = new Date(b.date).getTime();
  const dateA = dateAMilliseconds - (dataAOffset || 0);
  const dateB = dateBMilliseconds - (dataBOffset || 0);
  
  if (dateA < dateB) return -1;
  if (dateA > dateB) return 1;
  return idMod;
}

export const sortTwoSlidesByDatetime = (a: Slide, b: Slide) => {
  const idMod = (a.id || '') < (b.id || '') ? -1 : 1;
  if (!a.date && !b.date) return idMod;
  if (!a.date) return -1;
  if (!b.date) return 1;

  const dateA = new Date(new Date(a.date).toDateString()).getTime();
  const dateB = new Date(new Date(b.date).toDateString()).getTime();

  if (dateA < dateB) return -1;
  if (dateA > dateB) return 1;

  if (!a.time && !b.time) return idMod;
  if (a.time === b.time) return idMod;
  if (!a.time) return -1;
  if (!b.time) return 1;

  // Convert times to comparable values (e.g., minutes since midnight)
  const timeA = a.time.toLowerCase().trim();
  const timeB = b.time.toLowerCase().trim();

  const aTime = getTimeFromStr(timeA);
  const bTime = getTimeFromStr(timeB);
  const aNumber = aTime?.hoursNumber ?
    aTime?.hoursNumber * 3600 + (aTime?.minutesNumber || 0) * 60 + (aTime?.secondsNumber || 0) :
    0;
  const bNumber = bTime?.hoursNumber ?
    bTime?.hoursNumber * 3600 + (bTime?.minutesNumber || 0) * 60 + (bTime?.secondsNumber || 0) :
    0;
  // First sort the dates
  if (aNumber < bNumber) return -1;
  if (aNumber > bNumber) return 1;
  return idMod;
}

export const sortTwoSlidesByDisplayOrder = (a: Slide, b: Slide) => {
  if (!a.displayOrder) return -1;
  if (!b.displayOrder) return 1;
  return a.displayOrder - b.displayOrder;
}

export const getSortedSlides = (unsortedSlides: Slide[], sortBy: 'displayOrder' | 'datetime' = 'displayOrder'): Slide[] => {
  const sortedSlides = unsortedSlides.sort((a: Slide, b: Slide) => {
    if (sortBy === 'displayOrder') {
      return sortTwoSlidesByDisplayOrder(a, b);
    } else {
      return sortTwoSlidesByDatetime(a, b);
    }
  });

  let displayOrder = 0;
  for (const slide of sortedSlides) {
    displayOrder += 1;
    slide.displayOrder = displayOrder;
    slide.order = displayOrder;
    slide.show = slide.show !== undefined ? slide.show : true;
  }
  return sortedSlides;
}
